import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { ApiError, queryClient } from "@/lib/queryClient";
import { isEmbeddedInApp } from "@/lib/native-bridge";
import { saveRequester } from "@/site/components/birthday-request/br-data";
import {
  CAKE_UNRESOLVABLE,
  cakeKeyForMessage,
  cakeKeyForSender,
  cakeStatusKeys,
  postCake,
  reconcileAnonSigner,
} from "@/site/components/birthday-request/funnel-data";
import {
  clearPendingBrAction,
  liveCakeWalls,
  peekPendingBrAction,
} from "@/site/components/birthday-request/pending-br-action";

// Keyed by user id so a different account logging in on the same tab reconciles too.
const RECONCILED_FLAG = "bday_reconciled";

/**
 * Refresh only what these two jobs can actually change.
 *
 * This used to be a bare `invalidateQueries()`, which matches EVERY key — it
 * threw away the whole cache including `/api/user/auth/me`, so finishing a
 * funnel action triggered a full refetch storm and a flash of the loading
 * screen at exactly the moment the member had just signed in.
 */
function invalidateFunnelData(): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["/api/user/reminders"] }),
    queryClient.invalidateQueries({ queryKey: ["/api/user/contacts"] }),
    queryClient.invalidateQueries({ queryKey: ["/api/user/card-sends"] }),
    queryClient.invalidateQueries({ queryKey: ["/api/user/my-cards"] }),
  ]).then(() => undefined);
}

/**
 * Runs once when the viewer becomes authenticated. Two jobs that both need "just got a session":
 * (1) reconcile any anonymous signatures from this browser to the account (PRD 05 D11 — idempotent,
 * once per login per tab); (2) finish a Birthday-Request funnel action the user started while
 * logged out (save the requester's birthday, or a cake request), which `pending-br-action` stashed
 * before sending them to sign in.
 *
 * Mounted in `__root.tsx`, NOT in `SiteLayout` (moved 2026-08-18). Social sign-in now completes in
 * a popup and leaves the member where they were, so this can no longer rely on them being bounced
 * to `/` — and `SiteLayout` doesn't cover the chrome-free routes, including `/card/read/*`, which
 * is exactly where the cake dialog stashes its action.
 */
export function usePostAuthFunnel(): void {
  // An email-link soft session counts: reconcile and the stashed cake / save-requester
  // calls all accept it (Nick 10-13 — a signer arriving from an email is signed in).
  const { user, softUser } = useAuth();
  const userId = (user ?? softUser)?.user_id ?? null;

  useEffect(() => {
    if (!userId) return;

    // (1) Attach anonymous signatures — once per account per tab; cleared on failure so the
    // next login retries (the anon cookie survives). sessionStorage throws in Safari Private
    // Mode, so guard every access: a throw on read is treated as "flag not set" (best-effort
    // set below) rather than aborting the whole effect and stranding the funnel resume in (2).
    const reconcileKey = `${RECONCILED_FLAG}:${userId}`;
    let alreadyReconciled = false;
    try {
      alreadyReconciled = !!sessionStorage.getItem(reconcileKey);
    } catch {
      /* private-mode — treat as not reconciled */
    }
    if (!alreadyReconciled) {
      try {
        sessionStorage.setItem(reconcileKey, "1");
      } catch {
        /* private-mode — best-effort, reconcile still runs below */
      }
      reconcileAnonSigner()
        .then((r) => {
          if (r.reconciled && (r.messages > 0 || r.signerRows > 0)) {
            void invalidateFunnelData();
          }
        })
        .catch(() => {
          try {
            sessionStorage.removeItem(reconcileKey); // non-fatal — retry on a later login
          } catch {
            /* private-mode — nothing was persisted anyway */
          }
        });
    }

    // (2) Resume a pending funnel action (only fires once — cleared before running).
    const pending = peekPendingBrAction();
    if (!pending) return;
    // A cake whose authwall is still open on the page handles its own resume.
    if (pending.kind === "cake" && liveCakeWalls.size > 0) return;
    clearPendingBrAction();

    if (pending.kind === "save-requester") {
      saveRequester(pending.token)
        .then(() => {
          void invalidateFunnelData();
          // Same landing as the signed-in tap (legacy /birthday/request/success): the
          // congrats + get-more-birthdays page. The app webview stays put.
          if (pending.name && !isEmbeddedInApp()) {
            window.location.assign(
              `/reminders/birthday/add?from=br&name=${encodeURIComponent(pending.name)}`,
            );
            return;
          }
          toast({ variant: "success", description: `Saved ${pending.label} to your reminders.` });
        })
        .catch((err) =>
          toast({
            description:
              err instanceof ApiError ? err.message : "We couldn't save that. Try again.",
            variant: "destructive",
          }),
        );
    } else {
      postCake(
        pending.target ??
          (pending.cardSendMessageId
            ? cakeKeyForMessage(pending.cardSendMessageId)
            : cakeKeyForSender(pending.cardSendId ?? "")),
        pending.surface,
      )
        .then(() => {
          void invalidateFunnelData();
          void queryClient.invalidateQueries({ queryKey: cakeStatusKeys.all });
          toast({
            variant: "success",
            description: `Birthday request sent — ${pending.label} lands once they answer.`,
          });
        })
        .catch((err) => {
          const detail = err instanceof ApiError ? err.message : "";
          toast({
            description:
              detail === CAKE_UNRESOLVABLE
                ? "We couldn't find an address for that person."
                : detail || "We couldn't send that request.",
            variant: "destructive",
          });
        });
    }
  }, [userId]);
}
